import { useCallback, useRef, useEffect } from 'react';

export type RefreshEventType = 
  | 'student-updated'
  | 'student-created' 
  | 'student-deleted'
  | 'bulk-import'
  | 'payment-status-changed'
  | 'membership-changed'
  | 'ai-risk-recalculated'
  | 'status-synced'
  | 'family-changed';

export interface RefreshEvent {
  type: RefreshEventType;
  studentId?: string;
  data?: any;
  timestamp: number;
}

export interface UseStudentRefreshOptions {
  onRefresh: () => Promise<void>;
  debounceMs?: number;
}

export const useStudentRefresh = ({ onRefresh, debounceMs = 100 }: UseStudentRefreshOptions) => {
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingEventsRef = useRef<RefreshEvent[]>([]);

  // Emit refresh event
  const emitRefreshEvent = useCallback((type: RefreshEventType, studentId?: string, data?: any) => {
    const event: RefreshEvent = {
      type,
      studentId,
      data,
      timestamp: Date.now()
    };

    // Add to pending events
    pendingEventsRef.current.push(event);

    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Set new timeout for batched refresh
    refreshTimeoutRef.current = setTimeout(async () => {
      const events = [...pendingEventsRef.current];
      pendingEventsRef.current = [];

      // Determine refresh strategy based on events
      const needsFullRefresh = events.some(event => 
        event.type === 'bulk-import' || 
        event.type === 'student-deleted' ||
        events.length > 3 // Too many events, just refresh everything
      );

      if (needsFullRefresh) {
        await onRefresh();
      } else {
        // For single student updates, we could implement optimistic updates here
        // For now, we'll do a full refresh but this could be optimized
        await onRefresh();
      }
    }, debounceMs);
  }, [onRefresh, debounceMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return {
    emitRefreshEvent,
    // Convenience methods for common operations
    refreshStudent: (studentId: string, data?: any) => 
      emitRefreshEvent('student-updated', studentId, data),
    refreshCreated: (studentId: string, data?: any) => 
      emitRefreshEvent('student-created', studentId, data),
    refreshDeleted: (studentId: string) => 
      emitRefreshEvent('student-deleted', studentId),
    refreshBulkImport: () => 
      emitRefreshEvent('bulk-import'),
    refreshPaymentStatus: (studentId: string) => 
      emitRefreshEvent('payment-status-changed', studentId),
    refreshMembership: (studentId: string) => 
      emitRefreshEvent('membership-changed', studentId),
    refreshAIRisk: (studentId: string) => 
      emitRefreshEvent('ai-risk-recalculated', studentId),
    refreshStatus: (studentId: string) => 
      emitRefreshEvent('status-synced', studentId),
    refreshFamily: (studentId: string) => 
      emitRefreshEvent('family-changed', studentId),
  };
};

